import * as React from 'react'

import { inject, observer } from 'mobx-react'

import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import { DisabledResourceIdsContext } from './DisabledResourceIdsContext'

interface IProps {
  children: React.ReactNode

  locationAttributesStore?: LocationAttributesStore
  state?: InitialState
}

// Loads the bookable resource types once for the configurator and derives the
// set of ids no dependent selector may offer, so equipment, materials and any
// future type are excluded uniformly.
function DisabledResourceIdsProvider({
  children,
  locationAttributesStore,
  state,
}: IProps) {
  const projectId = state?.activeProject?.id
  const { records: equipment, isReady: isEquipmentReady } =
    useEquipmentResources({ projectId })
  const { records: materials } = useMaterialResources({ projectId })

  // Some dependent selectors still render the legacy location list. Until the
  // Equipment Resource store has settled, fail closed by hiding every legacy id;
  // otherwise those selectors could briefly expose non-bookable equipment. Read
  // the legacy list in render so the observer tracks it.
  const legacyEquipmentList =
    locationAttributesStore?.offloadingEquipmentsStore?.list ?? []

  const disabledResourceIds = React.useMemo(() => {
    // Workflow-type scoping now lives in each field's own allow-list, which these
    // selectors apply themselves; bookability is the only blanket exclusion left.
    const isVisible = (record: { root?: { isBookable?: boolean } }) =>
      !!record.root?.isBookable
    const ids = new Set<string>()
    if (isEquipmentReady) {
      for (const record of equipment) {
        if (!isVisible(record)) ids.add(record.id)
      }
    } else {
      for (const eq of legacyEquipmentList) ids.add(eq.id)
    }
    for (const record of materials) {
      if (!isVisible(record)) ids.add(record.id)
    }
    return ids
  }, [equipment, materials, isEquipmentReady, legacyEquipmentList])

  return (
    <DisabledResourceIdsContext.Provider value={disabledResourceIds}>
      {children}
    </DisabledResourceIdsContext.Provider>
  )
}

export default inject(
  'locationAttributesStore',
  'state',
)(observer(DisabledResourceIdsProvider))
